import { useState, useRef, useCallback, useEffect } from 'react';
import { datasets as datasetsApi, sessions as sessionsApi } from '../api/client';

const SPEED_INTERVALS = {
  1: 1000,
  2: 500,
  5: 200,
  10: 100,
  25: 40,
  50: 20,
};

const SPEEDS = [1, 2, 5, 10, 25, 50];

const SAVE_INTERVAL = 30;

export default function useReplayEngine(sessionId) {
  const [allCandles, setAllCandles] = useState([]);
  const [visibleCandles, setVisibleCandles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalBars, setTotalBars] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [atEnd, setAtEnd] = useState(false);

  const intervalRef = useRef(null);
  const allCandlesRef = useRef([]);
  const currentIndexRef = useRef(0);
  const lastSavedIndex = useRef(0);
  const sessionRef = useRef(null);

  const formatCandles = (raw) => raw.map((c) => ({
    time: Math.floor(new Date(c.timestamp).getTime() / 1000),
    open: typeof c.open === 'number' ? c.open : parseFloat(c.open),
    high: typeof c.high === 'number' ? c.high : parseFloat(c.high),
    low: typeof c.low === 'number' ? c.low : parseFloat(c.low),
    close: typeof c.close === 'number' ? c.close : parseFloat(c.close),
    volume: typeof c.volume === 'number' ? c.volume : parseFloat(c.volume),
  }));

  // Load ALL candles before starting replay
  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      try {
        const sessionRes = await sessionsApi.get(sessionId);
        if (cancelled) return;
        const sess = sessionRes.data;
        setSession(sess);
        sessionRef.current = sess;

        const fetchStart = sess.start_bar_index || 0;
        const endBarIndex = sess.end_bar_index;
        const limit = 50000;
        let offset = fetchStart;
        let accumulated = [];
        let datasetTotal = 0;

        // Load all chunks
        while (true) {
          const res = await datasetsApi.candles(sess.dataset, offset, limit);
          if (cancelled) return;
          accumulated = accumulated.concat(res.data.candles);
          datasetTotal = res.data.total;
          offset += res.data.candles.length;
          if (res.data.candles.length < limit || offset >= datasetTotal) break;
        }

        // Trim to end_bar_index if set
        if (endBarIndex && accumulated.length > (endBarIndex - fetchStart)) {
          accumulated = accumulated.slice(0, endBarIndex - fetchStart);
        }

        if (cancelled) return;

        const formatted = formatCandles(accumulated);
        allCandlesRef.current = formatted;
        setAllCandles(formatted);
        setTotalBars(formatted.length);

        // Set initial position
        const startIdx = (sess.current_bar_index || sess.start_bar_index || 0) - fetchStart;
        const initialVisible = Math.min(Math.max(startIdx, 50), formatted.length);
        currentIndexRef.current = initialVisible;
        lastSavedIndex.current = initialVisible;
        setCurrentIndex(initialVisible);
        setVisibleCandles(formatted.slice(0, initialVisible));
        setAtEnd(initialVisible >= formatted.length);
      } catch (err) {
        if (!cancelled) console.error('Failed to load replay data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Save progress to server
  const saveProgress = useCallback(async (index) => {
    if (!sessionRef.current) return;
    try {
      const absIndex = (sessionRef.current.start_bar_index || 0) + index;
      await sessionsApi.updateProgress(sessionRef.current.id, absIndex);
      lastSavedIndex.current = index;
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  }, []);

  // Advance one bar — uses refs only, no stale closures
  const advanceOneRef = useRef(null);
  advanceOneRef.current = () => {
    const candles = allCandlesRef.current;
    const nextIdx = currentIndexRef.current + 1;

    if (nextIdx > candles.length) {
      setPlaying(false);
      setAtEnd(true);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return false;
    }

    currentIndexRef.current = nextIdx;
    setCurrentIndex(nextIdx);
    setVisibleCandles(candles.slice(0, nextIdx));
    if (nextIdx >= candles.length) setAtEnd(true);

    // Periodic save
    if (nextIdx - lastSavedIndex.current >= SAVE_INTERVAL) {
      saveProgress(nextIdx);
    }

    return true;
  };
  const advanceOne = useCallback(() => advanceOneRef.current(), []);

  // Start interval helper — always uses fresh advanceOne via ref
  const startInterval = useCallback((spd) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!advanceOneRef.current()) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, SPEED_INTERVALS[spd] || 1000);
  }, []);

  // Play
  const play = useCallback(() => {
    if (currentIndexRef.current >= allCandlesRef.current.length) return;
    setPlaying(true);
    startInterval(speed);
  }, [speed, startInterval]);

  // Pause
  const pause = useCallback(() => {
    setPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    saveProgress(currentIndexRef.current);
  }, [saveProgress]);

  // Step forward (one bar)
  const stepForward = useCallback(() => {
    if (playing) return;
    advanceOne();
  }, [playing, advanceOne]);

  // Step backward (one bar)
  const stepBackward = useCallback(() => {
    if (playing) return;
    const candles = allCandlesRef.current;
    const targetIdx = Math.max(1, currentIndexRef.current - 1);
    currentIndexRef.current = targetIdx;
    setCurrentIndex(targetIdx);
    setVisibleCandles(candles.slice(0, targetIdx));
    setAtEnd(false);
  }, [playing]);

  // Skip backward N bars
  const skipBackward = useCallback((n) => {
    const candles = allCandlesRef.current;
    if (playing) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setPlaying(false);
    }
    const targetIdx = Math.max(1, currentIndexRef.current - n);
    currentIndexRef.current = targetIdx;
    setCurrentIndex(targetIdx);
    setVisibleCandles(candles.slice(0, targetIdx));
    setAtEnd(false);
    saveProgress(targetIdx);
  }, [playing, saveProgress]);

  // Skip forward N bars
  const skipForward = useCallback((n) => {
    const candles = allCandlesRef.current;
    const wasPaused = !playing;
    if (playing) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const targetIdx = Math.min(currentIndexRef.current + n, candles.length);
    currentIndexRef.current = targetIdx;
    setCurrentIndex(targetIdx);
    setVisibleCandles(candles.slice(0, targetIdx));
    setAtEnd(targetIdx >= candles.length);
    saveProgress(targetIdx);

    if (!wasPaused && targetIdx < candles.length) {
      setPlaying(true);
      startInterval(speed);
    } else if (targetIdx >= candles.length) {
      setPlaying(false);
    }
  }, [playing, speed, startInterval, saveProgress]);

  // Change speed
  const changeSpeed = useCallback((newSpeed) => {
    setSpeed(newSpeed);
    if (playing && intervalRef.current) {
      startInterval(newSpeed);
    }
  }, [playing, startInterval]);

  // Go to specific index
  const goToIndex = useCallback((idx) => {
    const candles = allCandlesRef.current;
    const targetIdx = Math.max(1, Math.min(idx, candles.length));
    if (playing) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setPlaying(false);
    }
    currentIndexRef.current = targetIdx;
    setCurrentIndex(targetIdx);
    setVisibleCandles(candles.slice(0, targetIdx));
    setAtEnd(targetIdx >= candles.length);
    saveProgress(targetIdx);
  }, [playing, saveProgress]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      // Save on unmount
      if (sessionRef.current && currentIndexRef.current !== lastSavedIndex.current) {
        const absIdx = (sessionRef.current.start_bar_index || 0) + currentIndexRef.current;
        sessionsApi.updateProgress(sessionRef.current.id, absIdx).catch(() => {});
      }
    };
  }, []);

  return {
    allCandles,
    visibleCandles,
    currentIndex,
    totalBars,
    speed,
    speeds: SPEEDS,
    playing,
    loading,
    session,
    atEnd,
    play,
    pause,
    stepForward,
    stepBackward,
    skipForward,
    skipBackward,
    changeSpeed,
    goToIndex,
  };
}
